import {useCallback, useRef} from 'react';

import {ElementMouseEvent} from 'util/Types';

export interface MouseDelta {
  deltaX: number;
  deltaY: number;
}

export interface UseDnDMouseMoveHandlerInput {
  setMouseDelta: (mouseDelta: MouseDelta) => void;
}

export interface UseDnDMouseMoveHandlerOutput {
  handleMouseMove: (e: MouseEvent) => void;
  setMousePosition: (e: ElementMouseEvent) => void;
}

export default function useDnDMouseMoveHandler({
  setMouseDelta,
}: UseDnDMouseMoveHandlerInput): UseDnDMouseMoveHandlerOutput {
  const previousMousePosition = useRef({clientX: 0, clientY: 0});

  const handleMouseMove = useCallback(
    (event: MouseEvent) => {
      // To calculate the accurate dragDelta, which is the position of a dragged block relative to the
      // page/component while dragging, the parent hook depends on this hook sending vectors explaining how the
      // mouse has moved as opposed to it's actual position. To do this, we essentially keep track of the
      // current and previous clientX/Y position, and calculate the difference with the current clientX/Y position.
      const mouseDelta = {
        deltaX: event.clientX - previousMousePosition.current.clientX,
        deltaY: event.clientY - previousMousePosition.current.clientY,
      };

      setMouseDelta(mouseDelta);

      previousMousePosition.current = {
        clientX: event.clientX,
        clientY: event.clientY,
      };
    },
    [setMouseDelta]
  );

  const setMousePosition = useCallback((event: ElementMouseEvent) => {
    previousMousePosition.current = {
      clientX: event.clientX,
      clientY: event.clientY,
    };
  }, []);

  return {
    handleMouseMove,
    setMousePosition,
  };
}
